import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CacheService } from 'ionic-cache';
import { Router } from '@angular/router';
import { API_URL } from '../../environments/environment';
import {BehaviorSubject, from, Subject} from 'rxjs';
import { mergeMap, share } from 'rxjs/operators';
import { FeedbackService } from './feedback.service';

@Injectable({
  providedIn: 'root'
})
export class CentralAuthenticationService {

  public $current = new BehaviorSubject(undefined);
  public $tenantConfiguration = new BehaviorSubject({});
  public $currentUserEmail = new BehaviorSubject('');

  private tenantUrl = 'tenants';
  private tenantCacheUrl = 'current-tenant';
  private tokenCacheUrl = 'central-token';
  private tokenSuffix = '-token';
  private groupKey = 'central-auth';
  private configCacheUrl = 'tenant-configuration';
  constructor(private http: HttpClient,
              private cache: CacheService,
              private router: Router,
              private feedbackService: FeedbackService) {
    this.loadCurrent();
    this.loadConfiguration();
  }

  register(email, password) {
    const data = {
      email,
      password
    };

    const request = this.http.post(API_URL + 'register', data).pipe(share());
    request.subscribe(
      res => {
      this.http.post(API_URL + 'login', data).subscribe(() => {
        this.feedbackService.message(
          'We created an Account for you.',
          'Successfully Registered',
          5000);
      });
      },
      error => { this.feedbackService.standardError(error); });
    return request;
  }

  createTenant(form) {
    const formData = new FormData();
    for (const key of Object.keys(form.value)) {
      let value = form.value[key];
      if (value !== undefined && value !== null) {
        if (value instanceof Object) {
          value = JSON.stringify(value);
        }
        formData.append(key, value);
      }
    }
    const request = this.http.post(API_URL + 'create', formData).pipe(share());
    request.subscribe(
      res => {},
      error => { this.feedbackService.standardError(error); });
    return request;
  }

  login(email, password) {
    const data = {
      email,
      password
    };
    const request = this.http.post(API_URL + 'login', data);
    this.cache.loadFromObservable(this.tokenCacheUrl, request, this.groupKey, 60 * 60 * 24 * 4).subscribe(
      token => {
        this.$currentUserEmail.next(token.email);
        if (token.tenant_token) {
          this.storeTenant(token.tenant_token.tenant).then(
            () => {
              this.cache.saveItem(
                token.tenant_token.domains[0].domain + this.tokenSuffix,
                token.tenant_token,
                this.groupKey
              ).then(() => {
                this.getConfiguration(token.tenant_token.tenant.id);
                this.router.navigateByUrl('home');
              });
            }
          );
        } else if (token.central) {
          this.router.navigateByUrl('launch');
        }
      },
      error => {
        this.feedbackService.standardError(error);
        console.log(error);
      }
    );
  }

  public requestReset(email) {
    const request = this.http.get<any>(API_URL + 'request-password-reset/' + email).pipe(share());
    return request;
  }

  public resetPassword(formData) {
    const request = this.http.post(API_URL + 'reset-password', formData);
    return request;
  }

  public updateTenant(tenant) {
    const data = {
      id: tenant.id
    };
    return from(this.getToken()).pipe(mergeMap(token => {
      this.$currentUserEmail.next(token.email);
      const headers = new HttpHeaders({
        authorization: token.token_type + ' ' + token.access_token,
      });
      return this.http.post(API_URL + this.tenantUrl + '/select', data, { headers }).pipe(share());
    })).subscribe(
      ten => {
        this.storeTenant(ten).then(
          () => {
            this.tenantLogin();
          }
        );
        this.getConfiguration(tenant.id);
      },
      error => {
        console.log(error);
      }
    );
  }

  public tenantLogin() {
    Promise.all([
      this.getToken(),
      this.getTenant()
    ]).then(
      res => {
        const token = res[0];
        const tenant = res[1];
        const headers = new HttpHeaders({
          authorization: token.token_type + ' ' + token.access_token,
        });
        this.http.get(API_URL + this.tenantUrl + '/tenant_login/' + tenant.id, { headers }).subscribe(
          tenantToken => {
            this.cache.saveItem(tenantToken['domains'][0].domain + '-token', tenantToken, 'tenant-auth').then(
              () => {
                this.getConfiguration(tenantToken['tenant'].id);
                this.router.navigateByUrl('home');
              }
            );
          }
        );
      }
    );
  }

  public getConfiguration(tenantId) {
    const call = from(this.getToken()).pipe(mergeMap(token => {
      this.$currentUserEmail.next(token.email);
      const headers = new HttpHeaders({
        authorization: token.token_type + ' ' + token.access_token,
      });
      return this.http.get(API_URL + this.tenantUrl + '/configuration/' + tenantId, { headers }).pipe(share());
    }));
    call.subscribe(
      res => {
        this.storeConfiguration(res);
      });
    return call;
  }

  public storeConfiguration(config) {
    const prom = this.cache.saveItem(this.configCacheUrl, config, this.groupKey);
    prom.then(() => this.loadConfiguration());
    return prom;
  }

  public loadConfiguration() {
    this.cache.getItem(this.configCacheUrl).then(
      config => {
        this.$tenantConfiguration.next(config);
      }
    ).catch(err => {});
  }

  public storeTenant(tenant) {
    const prom = this.cache.saveItem(this.tenantCacheUrl, tenant, this.groupKey);
    prom.then(() => {
      this.loadCurrent();
    });
    return prom;
  }

  public removeTenant() {
    this.$current.next(undefined);
    return this.cache.removeItem(this.tenantCacheUrl);
  }

  public loadCurrent() {
    this.getTenant().then(
      tenant => this.$current.next(tenant),
      error => console.log(error)
    );
  }

  async getTenant() {
    return await this.cache.getItem(this.tenantCacheUrl).catch(
      error => {

      }
    );
  }

  public logout() {
    return from(this.getToken()).pipe(mergeMap(token => {
      const headers = new HttpHeaders({
        authorization: token.token_type + ' ' + token.access_token,
      });
      const request =  this.http.get(API_URL + 'logout', {headers}).pipe(share());
      request.subscribe(
        () => {
          this.cache.clearAll().then(() => { console.log('cleared all data from cache.'); });
          this.removeToken();
        },
        error => {
          this.cache.clearGroup(this.groupKey);
        }
      );
      return request;
    }));
  }

  public isAuthenticated() {
    const result = new Subject<boolean>();
    this.getToken().then(
      token => {
        if (token) {
          const headers = {
            authorization: token.token_type + ' ' + token.access_token,
          };
          const request = this.http.get(API_URL + 'authenticate', { headers });
          const response = this.cache.loadFromDelayedObservable(
            'central-authenticate-' + token.email,
            request,
            this.groupKey,
            60
          ).pipe(share());
          response.subscribe(
            res => {
              result.next(true);
              result.complete();
            }, error => {
              this.feedbackService.standardError(error);
              this.removeToken();
              result.next(false);
              result.complete();
            }
          );
        } else {
          result.next(false);
          result.complete();
        }
      },
      error => {
        result.next(false);
        result.complete();
      }
    );
    return result.asObservable();
  }

  async getToken() {
    return await this.cache.getItem(this.tokenCacheUrl).catch(
      () => {
        this.removeToken();
        return false;
      }
    );
  }

  removeToken() {
    return this.cache.clearGroup(this.groupKey);
  }
}
